import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';

import type { PopupData, Service, ValidationErrors } from 'webapp-types';
import { PopupEvent } from 'webapp/enums/enums';
import { validateByRules } from 'webapp/utils/validate';

import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import FormCheckboxComponent from 'webapp/components/form/checkbox';
import FormInputComponent from 'webapp/components/form/input';
import type { Args } from 'webapp/components/popup/inner/modal';
import PopupInnerModalComponent from 'webapp/components/popup/inner/modal';
import RowErrorComponent from 'webapp/components/row-error';
import SubmitButtonComponent from 'webapp/components/submit-button';

export default class CommonPopupConfirmComponent<
	TData extends PopupData.Common.Confirm<(...args: never[]) => void> = PopupData.Common.Confirm,
> extends PopupInnerModalComponent<TData> {
	@service('user-settings') declare userSettingsService: Service.UserSettings;

	@tracked typedWord = '';
	@tracked errors: ValidationErrors<string> = {};
	@tracked dontShowAgain = false;

	wasConfirmed = false;

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		window.addEventListener('keydown', this.handleKeyDown);

		this.args.model.on(PopupEvent.CLOSING, this, () => {
			window.removeEventListener('keydown', this.handleKeyDown);
			if (!this.wasConfirmed) {
				this.onReject();
			}
		});
	}

	get title() {
		return this.popupData.title ?? 'Action required';
	}

	get titleClass(): string {
		return this.popupData.isWarning ? 'orange-on-transparent' : 'red-on-transparent';
	}

	get text() {
		return this.popupData.text;
	}

	get confirmAction() {
		return this.popupData.confirmAction ?? 'Yes';
	}

	get cancelAction() {
		return this.popupData.cancelAction ?? 'No';
	}

	get confirmWord() {
		return this.popupData.confirmWord || '';
	}

	get buttonTheme() {
		return this.popupData.isWarning ? 'blue' : 'red';
	}

	get isDontShowAgainCheckboxShown() {
		return !!this.popupData.dontShowAgainKey;
	}

	validate() {
		if (this.confirmWord) {
			const rule = { rule: 'match', field: 'confirmWord', opts: { insensitive: true } };
			this.errors = validateByRules(this, [rule], 'typedWord');
		}
		return Object.keys(this.errors).length === 0;
	}

	onReject() {
		const rejectFn = this.popupData.onReject;
		if (rejectFn) {
			rejectFn();
		}
	}

	confirmAndClose() {
		this.popupData.onConfirm();
		this.close();
	}

	@action
	confirm() {
		if (!this.validate()) {
			return;
		}

		if (!this.wasConfirmed) {
			this.wasConfirmed = true;
			this.confirmAndClose();
		}

		this.saveDontShowAgain();
	}

	@action
	saveAndClose() {
		this.saveDontShowAgain();
		this.close();
	}

	@action
	changeDontShowAgain(value: boolean) {
		this.dontShowAgain = value;
	}

	@action
	handleKeyDown(event: KeyboardEvent): void {
		if (event.key === 'Enter') {
			this.confirm();
		} else if (event.key === 'Escape') {
			this.saveAndClose();
		}
	}

	private saveDontShowAgain() {
		const hidePopupUserSettingsKey = this.popupData.dontShowAgainKey,
			value = this.dontShowAgain;

		if (hidePopupUserSettingsKey && value) {
			this.userSettingsService.saveConfirmPopupDontShowAgainValue(
				hidePopupUserSettingsKey,
				value,
			);
		}
	}
	<template>
		<div class="content">
			<div class="header {{this.titleClass}}">
				<CommonUiIconComponent @name="AlertFill" @size="lg" />
			</div>
			<span class="title">{{{this.title}}}</span>
			{{#if this.text}}
				<p>{{{this.text}}}</p>
			{{/if}}
			{{#if this.isDontShowAgainCheckboxShown}}
				<br />
				<div class="row">
					<FormCheckboxComponent
						@selected={{this.dontShowAgain}}
						@label="Don't show this message again"
						@onChange={{this.changeDontShowAgain}}
					/>
				</div>
			{{/if}}
			{{#if this.confirmWord}}
				<br />
				<span>Type {{this.confirmWord}} to confirm:</span>
				<div class="row">
					<FormInputComponent
						name="confirmword"
						maxlength="50"
						@value={{this.typedWord}}
						@insertNewline={{this.confirm}}
					/>
					<RowErrorComponent @items={{this.errors.typedWord}} />
				</div>
			{{/if}}
			<div class="controls2">
				<div class="controls2__right">
					<SubmitButtonComponent
						data-testid="test-confirmPopupCancelActionButton"
						@text={{this.cancelAction}}
						@theme="grey-empty"
						@noDelay={{true}}
						@submitInProgress={{this.submitInProgress}}
						@onTrigger={{this.saveAndClose}}
					/>
					<SubmitButtonComponent
						data-testid="test-confirmPopupConfirmActionButton"
						@text={{this.confirmAction}}
						@theme={{this.buttonTheme}}
						@noDelay={{true}}
						@submitInProgress={{this.submitInProgress}}
						@onTrigger={{this.confirm}}
					/>
				</div>
			</div>
		</div>
	</template>
}
